import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Plus, Trash2, Star, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const ServiceAlternativesDialog = ({ open, onClose, service, onSave }) => {
  const [alternatives, setAlternatives] = useState(service?.alternatives || []);
  const [newAlt, setNewAlt] = useState({ title: '', description: '', price: '', isDefault: false });

  const addAlternative = () => {
    if (!newAlt.title || !newAlt.price) {
      toast.error("Titel und Preis sind erforderlich");
      return;
    }

    const alt = {
      id: Date.now().toString(),
      title: newAlt.title,
      description: newAlt.description,
      price: parseFloat(newAlt.price),
      isDefault: newAlt.isDefault
    };

    setAlternatives([...alternatives, alt]);
    setNewAlt({ title: '', description: '', price: '', isDefault: false });
  };

  const removeAlternative = (id) => {
    setAlternatives(alternatives.filter(a => a.id !== id));
  };

  const setDefault = (id) => {
    setAlternatives(alternatives.map(a => ({
      ...a,
      isDefault: a.id === id
    })));
  };

  const handleSave = () => {
    if (alternatives.length === 0) {
      toast.error("Bitte fügen Sie mindestens eine Alternative hinzu");
      return;
    }

    onSave(alternatives);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Alternativpositionen für: {service?.name}</DialogTitle>
          <p className="text-sm text-slate-600 mt-2">
            Erstellen Sie verschiedene Optionen für diese Leistung. Der Kunde kann wählen.
          </p>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Existing Alternatives */}
          {alternatives.length > 0 && (
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Vorhandene Alternativen:</h3>
              <div className="space-y-2">
                {alternatives.map((alt) => (
                  <div key={alt.id} className="p-4 border rounded-lg bg-slate-50 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-slate-800">{alt.title}</span>
                        {alt.isDefault && (
                          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
                            <Star className="w-3 h-3 mr-1" />
                            Standard
                          </Badge>
                        )}
                      </div>
                      {alt.description && (
                        <p className="text-sm text-slate-600 mb-2">{alt.description}</p>
                      )}
                      <div className="text-lg font-bold text-blue-600">
                        {alt.price.toLocaleString('de-DE', { minimumFractionDigits: 2 })}€
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!alt.isDefault && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setDefault(alt.id)}
                          title="Als Standard markieren"
                        >
                          <Star className="w-3 h-3" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => removeAlternative(alt.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Alternative */}
          <div className="border-t pt-6">
            <h3 className="font-semibold text-slate-800 mb-3">Neue Alternative hinzufügen:</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="alt-title">Titel *</Label>
                  <Input
                    id="alt-title"
                    value={newAlt.title}
                    onChange={(e) => setNewAlt({ ...newAlt, title: e.target.value })}
                    placeholder="z.B. Standard, Premium, Enterprise"
                  />
                </div>
                <div>
                  <Label htmlFor="alt-price">Preis *</Label>
                  <Input
                    id="alt-price"
                    type="number"
                    step="0.01"
                    value={newAlt.price}
                    onChange={(e) => setNewAlt({ ...newAlt, price: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="alt-description">Beschreibung (optional)</Label>
                <Textarea
                  id="alt-description"
                  value={newAlt.description}
                  onChange={(e) => setNewAlt({ ...newAlt, description: e.target.value })}
                  placeholder="Details zu dieser Option..."
                  rows={2}
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="alt-default"
                  checked={newAlt.isDefault}
                  onChange={(e) => setNewAlt({ ...newAlt, isDefault: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="alt-default" className="cursor-pointer">
                  Als Standard-Auswahl markieren
                </Label>
              </div>

              <Button onClick={addAlternative} className="w-full" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Alternative hinzufügen
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={alternatives.length === 0}>
              <Check className="w-4 h-4 mr-2" />
              Speichern ({alternatives.length} Alternative{alternatives.length !== 1 ? 'n' : ''})
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServiceAlternativesDialog;
